import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, ScrollText, Filter, ChevronDown, ChevronRight, ChevronLeft, Search, Calendar, User, ExternalLink, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

function fullTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (isNaN(ms)) return "";
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}

function actionColor(action) {
  if (!action) return "bg-gray-50 text-gray-600";
  if (action.includes("created") || action.includes("added")) return "bg-green-50 text-green-700";
  if (action.includes("deleted") || action.includes("cancelled") || action.includes("removed")) return "bg-red-50 text-red-600";
  if (action.includes("updated") || action.includes("changed") || action.includes("rescheduled")) return "bg-blue-50 text-blue-700";
  if (action.includes("refund") || action.includes("damage")) return "bg-amber-50 text-amber-700";
  if (action.includes("note")) return "bg-purple-50 text-purple-700";
  return "bg-[#9A7B4F]/10 text-[#9A7B4F]";
}

function entityLink(entity_type, entity_id) {
  if (!entity_type || !entity_id) return null;
  if (entity_type === "booking") return `/admin/bookings?id=${entity_id}`;
  if (entity_type === "appointment") return `/admin/appointments?id=${entity_id}`;
  if (entity_type === "customer") return `/admin/customers/${entity_id}`;
  if (entity_type === "trial_lead") return `/admin/trial-leads?id=${entity_id}`;
  return null;
}

function DiffBlock({ before, after }) {
  const beforeObj = before && typeof before === "object" ? before : null;
  const afterObj = after && typeof after === "object" ? after : null;
  if (!beforeObj && !afterObj) return null;
  const keys = Array.from(new Set([...Object.keys(beforeObj || {}), ...Object.keys(afterObj || {})])).sort();
  if (!keys.length) return null;
  const fmt = (v) => {
    if (v === undefined || v === null) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="mt-2 bg-[#FAF8F0]/50 border border-[#282828]/8 p-2.5 font-mono text-[10.5px]" data-testid="audit-diff">
      <table className="w-full">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-[#676664]/60">
            <th className="text-left pb-1 pr-2">Field</th>
            <th className="text-left pb-1 pr-2">Before</th>
            <th className="text-left pb-1">After</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => {
            const b = beforeObj?.[k];
            const a = afterObj?.[k];
            const changed = JSON.stringify(b) !== JSON.stringify(a);
            return (
              <tr key={k} className="align-top border-t border-[#282828]/5">
                <td className="py-1 pr-2 text-[#676664] whitespace-nowrap">{k}</td>
                <td className={`py-1 pr-2 break-all ${changed ? "text-red-600 line-through opacity-70" : "text-[#676664]/70"}`}>{fmt(b)}</td>
                <td className={`py-1 break-all ${changed ? "text-green-700 font-medium" : "text-[#676664]/70"}`}>{fmt(a)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DetailsBlock({ details }) {
  if (!details || typeof details !== "object" || !Object.keys(details).length) return null;
  return (
    <div className="mt-2 text-[10.5px] text-[#5A5A5A]">
      <div className="text-[9px] uppercase tracking-wider text-[#676664]/60 mb-0.5">Details</div>
      <div className="bg-white border border-[#282828]/8 p-2 font-mono text-[10.5px] whitespace-pre-wrap break-all">
        {Object.entries(details).map(([k, v]) => (
          <div key={k}><span className="text-[#9A7B4F]">{k}</span>: {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
        ))}
      </div>
    </div>
  );
}

function Row({ entry }) {
  const [open, setOpen] = useState(false);
  const link = entityLink(entry.entity_type, entry.entity_id);
  const hasDiff = (entry.before && Object.keys(entry.before).length) || (entry.after && Object.keys(entry.after).length);
  const hasDetails = entry.details && Object.keys(entry.details).length;
  const hasExpand = hasDiff || hasDetails;

  return (
    <div className="border-b border-[#282828]/8 last:border-0" data-testid={`audit-row-${entry.id}`}>
      <button onClick={() => hasExpand && setOpen(!open)} className={`w-full text-left px-3 py-2.5 ${hasExpand ? "hover:bg-[#FAF8F0]/40" : "cursor-default"}`}>
        <div className="flex items-center gap-3">
          <div className="w-4 flex-shrink-0">
            {hasExpand ? (open ? <ChevronDown className="w-3.5 h-3.5 text-[#9A7B4F]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#676664]/50" />) : null}
          </div>
          <span className={`text-[10px] px-2 py-0.5 uppercase tracking-wider whitespace-nowrap ${actionColor(entry.action)}`} data-testid="audit-action">
            {entry.action.replace(/_/g, " ")}
          </span>
          <div className="flex-1 min-w-0">
            {entry.entity_type ? (
              link ? (
                <Link to={link} onClick={(e) => e.stopPropagation()} className="text-[12px] text-[#9A7B4F] hover:underline inline-flex items-center gap-1" data-testid="audit-entity-link">
                  {entry.entity_type}: <span className="font-mono text-[11px]">{entry.entity_id}</span>
                  <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              ) : (
                <span className="text-[12px] text-[#676664]">{entry.entity_type}: <span className="font-mono text-[11px]">{entry.entity_id}</span></span>
              )
            ) : (
              <span className="text-[11px] text-[#676664]/50 italic">no linked entity</span>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[11px] text-[#282828]" title={fullTime(entry.timestamp)}>{relTime(entry.timestamp)}</div>
            <div className="text-[10px] text-[#676664]/60">{entry.actor}</div>
          </div>
        </div>
      </button>
      {open && hasExpand && (
        <div className="px-3 pb-3 ml-7" data-testid={`audit-detail-${entry.id}`}>
          <div className="text-[10px] text-[#676664]/60 mb-1">{fullTime(entry.timestamp)}{entry.ip_address ? ` · ${entry.ip_address}` : ""}{entry.override_reason ? ` · override: ${entry.override_reason}` : ""}</div>
          <DiffBlock before={entry.before} after={entry.after} />
          <DetailsBlock details={entry.details} />
        </div>
      )}
    </div>
  );
}

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ actions: [], actors: [], entity_counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    actor: "",
    action: "",
    entity_type: "",
    entity_id: "",
    date_from: "",
    date_to: "",
    search: "",
  });

  const fetchFacets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/audit-log/facets`, { headers: auth() });
      setFacets(r.data);
    } catch { /* non-fatal */ }
  }, []);

  const fetchPage = useCallback(async (overridePage) => {
    setRefreshing(true);
    try {
      const params = { page: overridePage ?? page, limit, ...filters };
      // Drop empty filters so URL is clean
      Object.keys(params).forEach(k => { if (params[k] === "" || params[k] == null) delete params[k]; });
      const r = await axios.get(`${API}/admin/audit-log`, { params, headers: auth() });
      setItems(r.data.items || []);
      setPages(r.data.pages || 1);
      setTotal(r.data.total || 0);
      if (overridePage != null) setPage(overridePage);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load audit log");
    }
    setRefreshing(false);
    setLoading(false);
  }, [page, limit, filters]);

  useEffect(() => { fetchFacets(); }, [fetchFacets]);
  useEffect(() => { fetchPage(); /* eslint-disable-next-line */ }, []);

  const apply = () => fetchPage(1);
  const reset = () => {
    setFilters({ actor: "", action: "", entity_type: "", entity_id: "", date_from: "", date_to: "", search: "" });
    setTimeout(() => fetchPage(1), 0);
  };
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target?.value ?? e });
  const onKeyEnter = (e) => { if (e.key === "Enter") apply(); };

  if (loading) return <AdminLoadingState message="Loading audit log..." />;

  return (
    <AdminLayout
      title="Audit Log"
      subtitle="Every admin mutation across bookings, appointments, customers and trials"
      actions={
        <Button variant="outline" onClick={() => fetchPage()} disabled={refreshing} className="text-[11px] h-8 px-4" data-testid="audit-refresh">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
          Refresh
        </Button>
      }
    >
      {/* Facet counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="bg-white border border-[#282828]/8 p-3" data-testid="facet-total">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Total events</div>
          <div className="text-[18px] text-[#282828] font-medium">{facets.total}</div>
        </div>
        {[
          { key: "booking", label: "Bookings" },
          { key: "appointment", label: "Appointments" },
          { key: "customer", label: "Customers" },
          { key: "trial_lead", label: "Trials" },
        ].map(e => (
          <div key={e.key} className="bg-white border border-[#282828]/8 p-3" data-testid={`facet-${e.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">{e.label}</div>
            <div className="text-[18px] text-[#282828] font-medium">{facets.entity_counts?.[e.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#282828]/8 p-3 mb-4 grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid="audit-filters">
        <div className="col-span-2 md:col-span-2">
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">Search</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#676664]/40" />
            <input value={filters.search} onChange={set("search")} onKeyDown={onKeyEnter} placeholder="action / id / email…" className="w-full border border-[#282828]/12 pl-7 pr-2 py-1.5 text-[11px]" data-testid="filter-search" />
          </div>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">Actor</label>
          <select value={filters.actor} onChange={set("actor")} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="filter-actor">
            <option value="">All actors</option>
            {(facets.actors || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">Action</label>
          <select value={filters.action} onChange={set("action")} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="filter-action">
            <option value="">All actions</option>
            {(facets.actions || []).map(a => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">Entity</label>
          <select value={filters.entity_type} onChange={set("entity_type")} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="filter-entity-type">
            <option value="">Any</option>
            <option value="booking">Booking</option>
            <option value="appointment">Appointment</option>
            <option value="customer">Customer</option>
            <option value="trial_lead">Trial lead</option>
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">From</label>
          <input type="date" value={filters.date_from} onChange={set("date_from")} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">To</label>
          <input type="date" value={filters.date_to} onChange={set("date_to")} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="filter-date-to" />
        </div>
        <div className="col-span-2 md:col-span-7 flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={reset} className="text-[11px] h-8 px-3" data-testid="filter-reset">Reset</Button>
          <Button onClick={apply} disabled={refreshing} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="filter-apply">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Filter className="w-3.5 h-3.5 mr-1" />}
            Apply
          </Button>
        </div>
      </div>

      {/* Result list */}
      <div className="bg-white border border-[#282828]/8" data-testid="audit-list">
        {items.length === 0 ? (
          <div className="text-center py-12">
            <ScrollText className="w-8 h-8 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />
            <p className="text-[13px] text-[#676664]/40">No events match your filters.</p>
          </div>
        ) : (
          items.map(e => <Row key={e.id} entry={e} />)
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-[11px] text-[#676664]/70">
        <div data-testid="audit-pagination-info">
          Showing {items.length === 0 ? 0 : (page - 1) * limit + 1}–{(page - 1) * limit + items.length} of {total}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => fetchPage(page - 1)} disabled={page <= 1 || refreshing} className="text-[11px] h-7 px-2" data-testid="audit-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <span className="text-[11px] tabular-nums">Page {page} / {pages}</span>
          <Button variant="outline" onClick={() => fetchPage(page + 1)} disabled={page >= pages || refreshing} className="text-[11px] h-7 px-2" data-testid="audit-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
